import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { FileText, Search, Heart, Loader2, Calendar, Trash2, Smartphone } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { recordsAPI } from '@/lib/api'
import { useToast } from '@/hooks/use-toast'
import type { HealthRecord } from '@/lib/types'

export default function RecordsPage() {
  const [records, setRecords] = useState<HealthRecord[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterType, setFilterType] = useState<'all' | 'personal' | 'medical'>('all')
  const { toast } = useToast()

  const fetchRecords = async () => {
    try {
      setIsLoading(true)
      const params: any = {}
      if (filterType !== 'all') params.type = filterType
      if (searchTerm) params.search = searchTerm

      const response = await recordsAPI.getRecords(params)
      setRecords(response.data.records)
    } catch (error: any) {
      console.error('Failed to fetch records:', error)
      toast({
        title: "Failed to load records",
        description: "Unable to fetch your health records.",
        variant: "destructive",
      })
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchRecords()
  }, [filterType, searchTerm])

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this record?')) return

    try {
      await recordsAPI.deleteRecord(id)
      toast({
        title: "Record deleted",
        description: "The health record has been successfully deleted.",
      })
      fetchRecords() // Refresh the list
    } catch (error: any) {
      toast({
        title: "Delete failed",
        description: error.response?.data?.message || "Failed to delete record",
        variant: "destructive",
      })
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  return (
    <div className="space-y-6">
      {/* Clean, Minimalist Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-3xl font-bold text-gray-900">
            Health Records
          </h1>
          <p className="text-gray-600">
            View and manage all your health records
          </p>
        </div>
      </div>

      {/* Search and Filters - Minimalist */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search records..."
            className="pl-10 h-10 border border-gray-200 bg-white text-gray-900"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value as 'all' | 'personal' | 'medical')}
          className="h-10 px-3 border border-gray-200 rounded-lg text-gray-900 text-sm cursor-pointer bg-white hover:border-gray-300"
        >
          <option value="all">All Records</option>
          <option value="personal">Personal Logs</option>
          <option value="medical">Medical Records</option>
        </select>
      </div>

      {/* Records List */}
      <div className="space-y-3">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600 mr-3" />
            <span className="text-gray-600">Loading your records...</span>
          </div>
        ) : records.length > 0 ? (
          records.map((record) => (
            <div 
              key={record.id} 
              className="flex items-start space-x-4 p-4 rounded-lg bg-white border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-all duration-200 group"
            >
              {/* Icon */}
              <div className="flex-shrink-0">
                <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
                  record.type === 'personal' 
                    ? 'bg-blue-100' 
                    : 'bg-gray-100'
                }`}>
                  {record.type === 'personal' ? (
                    <Heart className="h-5 w-5 text-blue-600" />
                  ) : (
                    <FileText className="h-5 w-5 text-gray-600" />
                  )}
                </div>
              </div>
              
              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">
                      {record.type === 'personal' 
                        ? (record.condition || 'Personal Health Log')
                        : (record.documentType || 'Medical Record')
                      }
                    </h3>
                    
                    {(record.notes || (record.type === 'medical' && record.recordDetails)) && (
                      <p className="text-gray-600 text-sm mt-1 line-clamp-2">
                        {record.notes || (record.type === 'medical' && record.recordDetails)}
                      </p>
                    )}
                    
                    <div className="flex flex-wrap items-center gap-2 mt-3">
                      <div className="flex items-center text-xs text-gray-500 bg-gray-50 px-2.5 py-1 rounded">
                        <Calendar className="h-3 w-3 mr-1" />
                        {formatDate(record.timestamp)}
                      </div>
                      
                      <Badge variant="outline" className="text-xs">
                        {record.type === 'personal' ? 'Personal' : 'Medical'}
                      </Badge>
                      
                      {record.mediaFileName && (
                        <Badge variant="secondary" className="text-xs">
                          📎 {record.mediaFileName}
                        </Badge>
                      )}
                    </div>
                  </div>
                  
                  {/* Delete Button */}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(record.id)}
                    className="text-gray-400 hover:text-red-600 ml-4 opacity-0 group-hover:opacity-100 transition-all duration-200 h-8 w-8 p-0"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-12">
            <div className="h-16 w-16 mx-auto bg-gray-100 rounded-lg flex items-center justify-center mb-4">
              <FileText className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {searchTerm || filterType !== 'all' ? 'No matching records' : 'No records yet'}
            </h3>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              {searchTerm || filterType !== 'all' 
                ? 'Try adjusting your search or filters'
                : 'Use the CareVault mobile app to add health records, log symptoms, and upload medical documents.'
              }
            </p>
            {!(searchTerm || filterType !== 'all') && (
              <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
                <Smartphone className="h-4 w-4" />
                <span>Download the app to get started</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}